import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { ArrowRightIcon, RocketLaunchIcon } from '@heroicons/react/24/outline';
import { cn, useResponsiveBreakPoints } from '@restworld/utils-common';
import { useWizard } from '@restworld/utils-common';
import { Button } from '@restworld/ui-ds-base';

import { WizardPauseSaveConfirmModal } from '../wizard-step.components';
import { useWorkerApplicationCxt } from '../../worker-application-wizard.context';
import { SparklesIcon } from '../svg';
import { PreviousButton } from '../form-inputs.components';
import { useWizardShowsSignupBoxes } from '../form-inputs.hooks/use-wizard-signup-boxes';

export const SubmitButton = ({ loading, entryTime }: { loading: boolean; entryTime?: number }) => {
  const { activeStep } = useWizard();
  const { isDesktop } = useResponsiveBreakPoints();
  const router = useRouter();
  const { watch } = useFormContext();

  const query = router?.['query'];
  const isSpontaneousEntry = useMemo(() => query?.['isSpontaneousEntry'] === 'true', [query]);
  const { setOverlayModalContent } = useWorkerApplicationCxt();
  const isTAndCEnabled = watch('tAndC');
  const showsSignupBoxes = useWizardShowsSignupBoxes();
  const { t } = useTranslation();
  const firstEl = useMemo(() => {
    if (activeStep === 11)
      return (
        <div className="block">
          <Button
            // Bordered outline (master's `secondary-outline`), not the borderless grey `secondary-neutral`.
            variant="tertiary"
            onClick={() =>
              setOverlayModalContent({
                visible: true,
                content: <WizardPauseSaveConfirmModal />,
              })
            }
          >
            {t('common:save_and_exit')}
          </Button>
        </div>
      );
    return (
      <div className="hidden md:block">
        <PreviousButton isMobile={false} entryTime={entryTime} />
      </div>
    );
  }, [activeStep, entryTime, setOverlayModalContent, t]);

  const isDisabled = useMemo(() => showsSignupBoxes && !isTAndCEnabled, [isTAndCEnabled, showsSignupBoxes]);
  return (
    <StyledElement isCenter={activeStep < 11}>
      {firstEl}
      <Button
        loading={loading}
        type="submit"
        fullwidth={!isDesktop && activeStep !== 11}
        disabled={isDisabled}
        testId="submit-btn"
      >
        <span className="flex items-center gap-1 cursor-pointer">
          {isSpontaneousEntry && activeStep === 5 ? (
            <>
              {t('common:send_your_application')}
              <RocketLaunchIcon className={`h-4 w-4 ${isDisabled ? 'text-gray-500' : 'text-primary-900'}`} />
            </>
          ) : (
            <>
              {activeStep === 13 ? t('common:view_profile') : t('common:continue')}
              {activeStep === 13 ? (
                <SparklesIcon />
              ) : (
                <ArrowRightIcon className={`h-4 w-4 ${isDisabled ? 'text-gray-400' : 'text-text-900'}`} />
              )}
            </>
          )}
        </span>
      </Button>
    </StyledElement>
  );
};
const StyledElement = ({
  isCenter,
  className,
  ...props
}: { isCenter: boolean } & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex gap-4 items-center',
      isCenter ? 'justify-center' : 'justify-between',
      'md:justify-between',
      className
    )}
    {...props}
  />
);
